"use client";

import React, { useEffect, useState } from "react";
import { Clock, Crown, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Metric {
  id: number;
  label: string;
  value: number;
  suffix: string;
  icon: React.ElementType;
  colorClass: string;
  glowClass: string;
}
const metrics: Metric[] = [{
  id: 1,
  label: "Total Impressions",
  value: 50000000,
  suffix: "+",
  icon: Shield,
  colorClass: "text-primary",
  glowClass: "bg-primary/10"
}, {
  id: 2,
  label: "Production Hours",
  value: 12400,
  suffix: "",
  icon: Clock,
  colorClass: "text-primary",
  glowClass: "bg-primary/10"
}, {
  id: 3,
  label: "Global Brands",
  value: 85,
  suffix: "",
  icon: Crown,
  colorClass: "text-primary",
  glowClass: "bg-primary/10"
}];
function AnimatedCounter({
  value,
  suffix,
  duration = 2500
}: {
  value: number;
  suffix: string;
  duration?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const end = value;
    const start = 0;
    if (start === end) {
      setCount(end);
      return;
    }
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * (end - start) + start));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    window.requestAnimationFrame(step);
  }, [isVisible, value, duration]);
  const formattedCount = new Intl.NumberFormat("en-US").format(count);
  return <span ref={ref}>
      {formattedCount}
      {suffix}
    </span>;
}
export default function VictoryMetrics() {
  return <section data-section-id="222425" id="by-the-numbers" className="bg-background text-foreground py-[100px] relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[150px] pointer-events-none translate-y-1/3" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {metrics.map((metric, i) => <Card key={metric.id} data-index={i} className="bg-secondary/20 backdrop-blur-none border-border/50 rounded-none relative overflow-hidden group transition-colors hover:border-primary/50">
              {}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <CardContent className="pt-12 pb-12 flex flex-col items-center justify-center text-center relative z-10">
                <div className="mb-8 relative">
                  {}
                  <div className={`absolute inset-0 blur-3xl rounded-full ${metric.glowClass} scale-150 transition-transform duration-700 group-hover:scale-175`} />
                  <metric.icon className={`w-14 h-14 relative z-10 ${metric.colorClass} drop-shadow-[0_0_8px_rgba(218,165,32,0.3)]`} strokeWidth={1.5} />
                </div>

                <div className="text-5xl lg:text-6xl font-mono font-bold mb-4 tracking-tighter text-foreground">
                  <AnimatedCounter value={metric.value} suffix={metric.suffix} />
                </div>

                <h3 className="text-muted-foreground font-mono font-medium uppercase tracking-widest text-xs">
                  {metric.label}
                </h3>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}